export interface Location {
  location_id: number;
  address: string;
  city: string;
}

export interface LatestStatusCollection {
  item_count: number;
  items: LatestStatusResponse[];
}

export interface LatestStatusResponse {
  type: 'static' | 'event';
  item: StaticStatus | EventBasedStatus;
}

export interface StaticStatus {
  end_interval_time: number;
  items: StatusItemResponse[];
  device_id: string;
  ldn: string | null;
  start_interval_time: number;
  location: Location;
}

export interface EventBasedStatus {
  most_recent_status_device: string;
  underlying_devices: SubDevice[];
  device_id: string;
  ldn: string | null;
  start_interval_time: number;
  location: Location;
}

export interface SubDevice {
  type: string;
  name: string;
  status_code: string;
  status_name: string;
  description: string;
  reported_at: number;
}

export interface StatusItemResponse {
  status_id?: string;
  status_code?: string;
  status_name?: string;
  additional_information?: string;
}

export interface AggregatedStatusResponse {
  cosmost_id: string;
  device_id: string;
  items: AggregatedStatusItem[];
}

export interface AggregatedStatusItem {
  start_interval_time: number;
  end_interval_time: number;
  items: StatusItemResponse[];
}

export interface StatusEventResponse {
  id: string;
  device_id: string;
  timestamp: number;
  device_alarm: string;
  value: string;
  description: string;
  priority?: 'high' | 'medium' | 'low';
}

export interface StatusEventCollectionResponse {
  item_count: number;
  items: StatusEventResponse[];
}

export interface DeviceRuleStatus {
  rule_name: string;
  enabled: boolean;
}

export interface ClosedStatusResponse {
  id: string;
  device_id: string;
  status: string;
  /** `system`, `xemex` for any Xemex employee, or the customer's own username. */
  closed_by: string;
  /** Epoch seconds; null for alarms closed before the backend stored it. */
  opened_at: number | null;
  /** Epoch seconds; null when the backend does not know when the alarm was closed. */
  closed_at: number | null;
  /** `manual` or `device_recovered`; null for alarms closed before the backend stored it. */
  closed_reason: string | null;
}

export interface ClosedStatusCollectionResponse {
  item_count: number;
  items: ClosedStatusResponse[];
}

export const CLOSED_REASONS = ['manual', 'device_recovered'] as const;
export type ClosedReason = (typeof CLOSED_REASONS)[number];

export interface AlarmRow {
  typeName: string; // i18n key, e.g. 'status.names.offline'
  /** Raw alarm-type-catalog key (e.g. "standard.1", "FRAUD_EVENT") when this row's display
   * name should come from the central AlarmTypeCatalogService instead of i18n — set for
   * closed alarms, which carry the backend catalog key as-is (see ClosedStatusResponse.status). */
  catalogKey?: string;
  priority: 'Low' | 'Medium' | 'High';
  /** Undefined for a closed alarm the backend stored no opening time for (`opened_at` null). */
  openedAt: Date | undefined;
  /** Undefined for an open alarm, or a closed one the backend stored no closing time for. */
  closedAt?: Date;
  /** Raw actor from the backend; label it with ActorNamePipe. */
  closedBy?: string;
  /** Only set for a reason the portal knows how to label. */
  closedReason?: ClosedReason;
  state: 'Open' | 'Closed';
  canClose: boolean;
  deviceId: string;
  subDeviceType?: string;
}

/** Mirrors the backend's `shared.mapping.alarm_type_catalog` — the single source of truth for
 * alarm-type labels. `label` is English, `label_nl` is Dutch (identical for cloud types like
 * `device_connection` that aren't sourced from a DLMS event log — those go through i18n
 * `status.names.*` instead, see AlarmsOverviewTabComponent.typeLabel). */
export interface AlarmType {
  key: string;
  category: string;
  default_priority: 'low' | 'medium' | 'high';
  label: string;
  label_nl: string;
}

export interface AlarmTypeCollection {
  items: AlarmType[];
}
